import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  Grid,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

// API hooks
import { 
  useGetPresentationByIdQuery, 
  useUpdatePresentationMutation,
  useDeletePresentationMutation,
} from '../../services/api';

// Redux
import { useSelector } from 'react-redux';
import { RootState } from '../../services/store';
import { Question, Presentation } from '../../services/slices/presentationSlice';

// Validation schema
const PresentationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  presenter: Yup.string().required('Presenter name is required'),
  description: Yup.string(),
  date: Yup.date().required('Date is required'),
});

const QuestionSchema = Yup.object().shape({
  text: Yup.string().required('Question text is required'),
  options: Yup.array()
    .of(Yup.string().required('Option cannot be empty'))
    .min(2, 'At least 2 options are required'),
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`edit-tabpanel-${index}`}
      aria-labelledby={`edit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const EditPresentation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // State
  const [tabValue, setTabValue] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question>({
    id: uuidv4(),
    text: '',
    type: 'multiple_choice',
    options: ['', ''],
    required: true,
    order: 0,
  });
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  // Redux state
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.attributes?.['custom:role'] === 'admin';
  
  // Fetch presentation
  const { 
    data: presentation, 
    isLoading: isLoadingPresentation, 
    error: presentationError 
  } = useGetPresentationByIdQuery(id || '');
  
  // Mutations
  const [updatePresentation, { isLoading: isUpdating }] = useUpdatePresentationMutation();
  const [deletePresentation, { isLoading: isDeleting }] = useDeletePresentationMutation();
  
  // Formik for presentation details - moved before conditional returns
  const formik = useFormik({
    initialValues: {
      title: '',
      presenter: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    },
    validationSchema: PresentationSchema,
    onSubmit: async (values) => {
      if (questions.length === 0) {
        setQuestionError('At least one question is required');
        setTabValue(1);
        return;
      }
      
      try {
        if (presentation) {
          const updatedPresentation = {
            id: presentation.id,
            ...values,
            status: presentation.status,
            questions,
            updatedAt: new Date().toISOString(),
          };
          
          await updatePresentation(updatedPresentation).unwrap();
          navigate(`/presentations/${presentation.id}`);
        }
      } catch (error) {
        console.error('Failed to update presentation:', error);
      }
    },
    enableReinitialize: true,
  });
  
  // Initialize form and questions when presentation data is loaded
  useEffect(() => {
    if (presentation) {
      formik.setValues({
        title: presentation.title,
        presenter: presentation.presenter,
        description: presentation.description || '',
        date: new Date(presentation.date).toISOString().split('T')[0],
      });
      
      setQuestions([...presentation.questions]);
    }
  }, [presentation]);
  
  // Check if user is admin
  if (!isAdmin) {
    return (
      <Container maxWidth="md">
        <Alert severity="warning" sx={{ mt: 4 }}>
          You do not have permission to edit presentations.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/presentations')}
          sx={{ mt: 2 }}
        >
          Back to Presentations
        </Button>
      </Container>
    );
  }
  
  if (isLoadingPresentation) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (presentationError || !presentation) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          Error loading presentation. Please try again later.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/presentations')}
          sx={{ mt: 2 }}
        >
          Back to Presentations
        </Button>
      </Container>
    );
  }
  
  // Tab handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Question handlers
  const validateQuestion = () => {
    try {
      QuestionSchema.validateSync(currentQuestion, { abortEarly: false });
      return true;
    } catch (error: any) {
      setQuestionError(error.errors?.[0] || 'Validation error');
      return false;
    }
  };
  
  const handleAddQuestion = () => {
    if (!validateQuestion()) return;
    
    const newQuestion = {
      ...currentQuestion,
      order: questions.length,
    };
    
    setQuestions([...questions, newQuestion]);
    setCurrentQuestion({
      id: uuidv4(),
      text: '',
      type: 'multiple_choice',
      options: ['', ''],
      required: true,
      order: questions.length + 1,
    });
    setQuestionError(null);
  };
  
  const handleUpdateQuestion = (index: number) => {
    if (!validateQuestion()) return;
    
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...currentQuestion,
      order: index,
    };
    
    setQuestions(updatedQuestions);
    setCurrentQuestion({
      id: uuidv4(),
      text: '',
      type: 'multiple_choice',
      options: ['', ''],
      required: true,
      order: questions.length,
    });
    setQuestionError(null);
  };
  
  const handleEditQuestion = (question: Question) => {
    setCurrentQuestion(question);
  };
  
  const handleDeleteQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    // Update order for remaining questions
    updatedQuestions.forEach((q, i) => {
      q.order = i;
    });
    setQuestions(updatedQuestions);
  };
  
  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentQuestion({
      ...currentQuestion,
      text: e.target.value,
    });
  };
  
  const handleOptionChange = (index: number, value: string) => {
    const updatedOptions = [...currentQuestion.options];
    updatedOptions[index] = value;
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions,
    });
  };
  
  const handleAddOption = () => {
    setCurrentQuestion({
      ...currentQuestion,
      options: [...currentQuestion.options, ''],
    });
  };
  
  const handleRemoveOption = (index: number) => {
    if (currentQuestion.options.length <= 2) {
      setQuestionError('At least 2 options are required');
      return;
    }
    
    const updatedOptions = currentQuestion.options.filter((_, i) => i !== index);
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions,
    });
    
    setQuestionError(null);
  };
  
  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentQuestion({
      ...currentQuestion,
      required: e.target.checked,
    });
  };
  
  // Delete presentation
  const handleDeletePresentation = async () => {
    try {
      await deletePresentation(presentation.id).unwrap();
      navigate('/presentations');
    } catch (error) {
      console.error('Failed to delete presentation:', error);
    }
  };
  
  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/presentations/${presentation.id}`)}
          sx={{ mb: 2 }}
        >
          Back to Presentation
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Edit Presentation
          </Typography>
          
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmDialogOpen(true)}
          >
            Delete Presentation
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="edit presentation tabs">
          <Tab label="Details" id="edit-tab-0" aria-controls="edit-tabpanel-0" />
          <Tab label="Questions" id="edit-tab-1" aria-controls="edit-tabpanel-1" />
        </Tabs>
      </Box>
      
      {/* Details Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3 }}>
          <Box component="form" noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="title"
                  name="title"
                  label="Presentation Title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  onBlur={formik.handleBlur}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="presenter"
                  name="presenter"
                  label="Presenter Name"
                  value={formik.values.presenter}
                  onChange={formik.handleChange}
                  error={formik.touched.presenter && Boolean(formik.errors.presenter)}
                  helperText={formik.touched.presenter && formik.errors.presenter}
                  onBlur={formik.handleBlur}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="date"
                  name="date"
                  label="Presentation Date"
                  type="date"
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  error={formik.touched.date && Boolean(formik.errors.date)}
                  helperText={formik.touched.date && formik.errors.date}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Description"
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  onBlur={formik.handleBlur}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </TabPanel>
      
      {/* Questions Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Questions ({questions.length})
          </Typography>
          
          {questionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {questionError}
            </Alert>
          )}
          
          {/* Question List */}
          {questions.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {questions.map((question, index) => (
                <Card key={question.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {index + 1}. {question.text}
                      </Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleEditQuestion(question)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteQuestion(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Box sx={{ pl: 3 }}>
                      {question.options.map((option, optIndex) => (
                        <Typography key={optIndex} variant="body2" sx={{ mb: 0.5 }}>
                          {String.fromCharCode(65 + optIndex)}. {option}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
          
          {/* Add/Edit Question Form */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {currentQuestion.id === questions.find(q => q.id === currentQuestion.id)?.id
                ? 'Edit Question'
                : 'Add New Question'}
            </Typography>
            
            <TextField
              fullWidth
              label="Question Text"
              value={currentQuestion.text}
              onChange={handleQuestionTextChange}
              margin="normal"
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Options
            </Typography>
            
            {currentQuestion.options.map((option, index) => (
              <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                <TextField
                  fullWidth
                  label={`Option ${String.fromCharCode(65 + index)}`}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  margin="dense"
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveOption(index)}
                  sx={{ ml: 1, mt: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddOption}
              sx={{ mt: 1 }}
            >
              Add Option
            </Button>
            
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentQuestion.required}
                    onChange={handleRequiredChange}
                  />
                }
                label="Required"
              />
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={
                  currentQuestion.id === questions.find(q => q.id === currentQuestion.id)?.id
                    ? () => handleUpdateQuestion(questions.findIndex(q => q.id === currentQuestion.id))
                    : handleAddQuestion
                }
              >
                {currentQuestion.id === questions.find(q => q.id === currentQuestion.id)?.id
                  ? 'Update Question'
                  : 'Add Question'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </TabPanel>
      
      {/* Save Button */}
      <Box sx={{ mt: 3, mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => formik.handleSubmit()}
          startIcon={isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isUpdating}
        >
          Save Changes
        </Button>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Delete Presentation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this presentation? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeletePresentation}
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditPresentation;
